import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Bus, Users, DollarSign, CheckCircle, Clock, 
  ArrowRight, TrendingUp, Calendar, Bell, AlertCircle, Send,
  Package, MapPin, Navigation, BellRing, Phone, Loader2, Radio
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayTrips, setTodayTrips] = useState([]);
  const [balanceNotifications, setBalanceNotifications] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [loading, setLoading] = useState(true);

  // Drop-off Luggage & Proximity state
  const [dropoffPoints, setDropoffPoints] = useState([]);
  const [dropoffSummary, setDropoffSummary] = useState(null);
  const [notifyingDropoff, setNotifyingDropoff] = useState(null);

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/login');
      return;
    }
    if (token) {
      fetchDashboardData();
    }
  }, [token, authLoading]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, tripsRes, allTripsRes, balanceRes, dropoffRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/trips?date=${new Date().toISOString().split('T')[0]}`),
        axios.get(`${API_URL}/trips`),
        axios.get(`${API_URL}/notifications/passengers-with-balance`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/dashboard/dropoff-luggage`).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      if (balanceRes?.data) {
        setBalanceNotifications(balanceRes.data);
      } else {
        setBalanceNotifications(null);
      }
      if (dropoffRes?.data) {
        setDropoffPoints(dropoffRes.data.dropoff_points || []);
        setDropoffSummary({
          total_active_dropoffs: dropoffRes.data.total_active_dropoffs || 0,
          total_active_luggage: dropoffRes.data.total_active_luggage || 0
        });
      }
      // Prefer today's trips, fallback to all trips sorted by booked seats / departure date
      if (tripsRes.data && tripsRes.data.length > 0) {
        setTodayTrips(tripsRes.data.slice(0, 5));
      } else {
        const sorted = (allTripsRes.data || []).sort((a, b) => {
          const bookedA = a.total_seats - a.available_seats;
          const bookedB = b.total_seats - b.available_seats;
          return bookedB - bookedA;
        });
        setTodayTrips(sorted.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setBalanceNotifications(null);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await axios.post(
        `${API_URL}/notifications/send-balance-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Dispatched ${res.data.reminders_sent} SMS balance reminders!`);
      fetchDashboardData();
    } catch (err) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleDropoffNotify = async (tripId, dropoffPoint) => {
    setNotifyingDropoff(dropoffPoint);
    try {
      const res = await axios.post(
        `${API_URL}/trips/${tripId}/dropoff-notify`,
        { dropoff_point: dropoffPoint },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `Alerts dispatched for ${dropoffPoint}!`);
      // Refresh dropoff data
      const refreshRes = await axios.get(`${API_URL}/dashboard/dropoff-luggage`);
      if (refreshRes?.data) {
        setDropoffPoints(refreshRes.data.dropoff_points || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trigger drop-off alert');
    } finally {
      setNotifyingDropoff(null);
    }
  };

  if (authLoading || loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-200 rounded-lg" />
            ))}
          </div>
          <div className="h-64 bg-slate-200 rounded-lg" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Station Dashboard
            </h1>
            <p className="text-slate-600">
              Welcome back, {user?.full_name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-sm">
              <Calendar className="h-3 w-3 mr-1" />
              {format(new Date(), 'EEEE, dd MMMM yyyy')}
            </Badge>
          </div>
        </div>

        {/* Passenger Balance System Notifications */}
        {balanceNotifications && balanceNotifications.total_passengers_with_balance > 0 && (
          <Card className="border-2 border-amber-400 bg-amber-50 shadow-md">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-amber-200 rounded-lg text-amber-800 mt-0.5">
                    <Bell className="h-5 w-5 animate-bounce" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-900 text-sm flex items-center gap-2">
                      <span>⚠️ Pending Passenger Balances Alert:</span>
                      <Badge className="bg-amber-500 text-white font-mono">
                        {balanceNotifications.total_passengers_with_balance} Passengers
                      </Badge>
                    </h3>
                    <p className="text-xs text-amber-800 mt-0.5">
                      Total Outstanding: <strong>${balanceNotifications.total_balance_usd?.toFixed(2)} USD</strong> • <strong>R{balanceNotifications.total_balance_zar?.toFixed(2)} ZAR</strong>
                    </p>
                    <p className="text-[11px] text-amber-700 mt-0.5">
                      Online bookings departing within 4 hours without payment will have their seats auto-released.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Button
                    size="sm"
                    onClick={handleSendReminders}
                    disabled={sendingReminders}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow"
                  >
                    <Send className="h-3.5 w-3.5 mr-1.5" />
                    {sendingReminders ? 'Sending...' : 'Send SMS Reminders'}
                  </Button>
                </div>
              </div>

              {/* List of pending balance passengers */}
              {balanceNotifications.passengers?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-200 flex flex-wrap gap-2 text-xs">
                  {balanceNotifications.passengers.slice(0, 6).map((p, idx) => (
                    <span key={idx} className="bg-white border border-amber-300 px-2.5 py-1 rounded text-amber-950 font-medium flex items-center gap-1.5 shadow-sm">
                      <span className="bg-amber-100 text-amber-900 font-bold px-1.5 py-0.2 rounded text-[11px]">
                        Seat {p.seat_number || 'N/A'}
                      </span>
                      <span>{p.name}</span>
                      <span className="font-bold text-red-700">
                        {p.currency} {p.balance?.toFixed(2)}
                      </span>
                      {p.dropoff_point && (
                        <span className="text-slate-500 text-[10px]">
                          📍 {p.dropoff_point}
                        </span>
                      )}
                    </span>
                  ))}
                  {balanceNotifications.passengers.length > 6 && (
                    <span className="text-xs text-amber-800 self-center font-bold">
                      +{balanceNotifications.passengers.length - 6} more
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-panashe" data-testid="stat-trips">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today's Trips</p>
                  <p className="font-heading text-3xl font-bold text-slate-900">
                    {stats?.todays_trips || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-panashe-blue/10 rounded-lg flex items-center justify-center">
                  <Bus className="h-6 w-6 text-panashe-blue" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-panashe" data-testid="stat-bookings">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Bookings</p>
                  <p className="font-heading text-3xl font-bold text-slate-900">
                    {stats?.todays_bookings || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-panashe-gold/20 rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-panashe-gold" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-panashe" data-testid="stat-checkedin">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Checked In</p>
                  <p className="font-heading text-3xl font-bold text-green-600">
                    {stats?.checked_in_passengers || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-panashe col-span-2 md:col-span-4" data-testid="stat-revenue">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                    </div>
                    <p className="text-sm font-medium text-slate-600">Today's Revenue</p>
                  </div>
                  {/* Total */}
                  <p className="font-heading text-2xl font-bold text-slate-900 leading-tight">
                    ${(stats?.revenue?.USD || 0).toFixed(2)}
                    {stats?.revenue?.ZAR > 0 && (
                      <span className="text-base font-normal text-slate-500 ml-2">+ R{(stats?.revenue?.ZAR || 0).toFixed(2)}</span>
                    )}
                  </p>
                  {/* Breakdown row */}
                  <div className="flex flex-wrap gap-3 mt-2">
                    <div className="flex items-center gap-1.5 bg-blue-50 rounded-md px-2 py-1">
                      <Users className="h-3.5 w-3.5 text-blue-500" />
                      <span className="text-xs text-blue-700 font-medium">
                        Passengers: <span className="font-bold">${(stats?.passenger_revenue?.USD || 0).toFixed(2)}</span>
                        {stats?.passenger_revenue?.ZAR > 0 && <span className="text-slate-400 ml-1">+ R{(stats?.passenger_revenue?.ZAR || 0).toFixed(2)}</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-purple-50 rounded-md px-2 py-1">
                      <Package className="h-3.5 w-3.5 text-purple-500" />
                      <span className="text-xs text-purple-700 font-medium">
                        Luggage: <span className="font-bold">${(stats?.luggage_revenue?.USD || 0).toFixed(2)}</span>
                        {stats?.luggage_revenue?.ZAR > 0 && <span className="text-slate-400 ml-1">+ R{(stats?.luggage_revenue?.ZAR || 0).toFixed(2)}</span>}
                      </span>
                    </div>
                  </div>
                  {/* Per-trip breakdown */}
                  {stats?.trips_revenue?.length > 0 && (
                    <div className="mt-3 border-t pt-2 space-y-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Per Trip Revenue</p>
                      {stats.trips_revenue.map((tr, i) => (
                        <div key={tr.trip_id || i} className="flex flex-col sm:flex-row sm:items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0 gap-1">
                          <span className="text-slate-700 font-medium truncate sm:max-w-[50%]">
                            {tr.origin} → {tr.destination}
                          </span>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap text-[11px] sm:text-xs">
                            <span className="text-blue-600 font-medium" title="Passenger revenue">
                              👤 ${(tr.passenger_revenue?.USD || 0).toFixed(2)}
                            </span>
                            <span className="text-purple-600 font-medium" title="Luggage revenue">
                              📦 ${(tr.luggage_revenue?.USD || 0).toFixed(2)}
                            </span>
                            <span className="font-bold text-slate-900">
                              = ${(tr.total_revenue?.USD || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button 
            className="h-20 btn-panashe flex flex-col items-center justify-center gap-2"
            onClick={() => navigate('/dashboard/scanner')}
            data-testid="quick-scanner"
          >
            <CheckCircle className="h-6 w-6" />
            <span>Scan Tickets</span>
          </Button>
          
          <Button 
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 border-2"
            onClick={() => navigate('/dashboard/search')}
            data-testid="quick-search"
          >
            <Users className="h-6 w-6" />
            <span>Search Passengers</span>
          </Button>
          
          <Button 
            variant="outline"
            className="h-20 flex flex-col items-center justify-center gap-2 border-2"
            onClick={() => todayTrips[0]?.id && navigate(`/dashboard/manifest/${todayTrips[0].id}`)}
            data-testid="quick-manifest"
          >
            <Bus className="h-6 w-6" />
            <span>View Manifest</span>
          </Button>
        </div>

        {/* Active Drop-off Points with Luggage & Real-time Proximity Alerts */}
        <Card className="card-panashe border-2 border-purple-200 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between pb-2 bg-gradient-to-r from-purple-50 to-indigo-50 border-b">
            <div>
              <CardTitle className="font-heading flex items-center gap-2 text-purple-950">
                <Package className="h-5 w-5 text-purple-600" />
                Luggage Drop-off Points &amp; Live Arrival Proximity
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">
                Auto-detected from Host's moving device GPS • SMS trigger to receivers &amp; passengers
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-purple-100 text-purple-800 border-purple-300">
                {dropoffPoints.length} Drop-off Point{dropoffPoints.length !== 1 ? 's' : ''}
              </Badge>
              {dropoffSummary?.total_active_luggage > 0 && (
                <Badge variant="outline" className="text-purple-700">
                  {dropoffSummary.total_active_luggage} Parcels
                </Badge>
              )}
              {dropoffSummary?.total_destination_luggage > 0 && (
                <Badge variant="outline" className="text-amber-700 border-amber-300">
                  {dropoffSummary.total_destination_luggage} At/Past Destination
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {dropoffPoints.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Package className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm font-medium">No active luggage in transit right now</p>
                <p className="text-xs text-slate-400 mt-1">Drop-off points and proximity alerts will show here when luggage is loaded on trips</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dropoffPoints.map((dp, idx) => (
                  <div
                    key={`${dp.trip_id}-${dp.dropoff_point}-${idx}`}
                    className={`p-3.5 rounded-lg border transition-all ${
                      dp.proximity_status === 'approaching'
                        ? 'bg-red-50/80 border-red-300 ring-1 ring-red-400'
                        : dp.proximity_status === 'at_station'
                        ? 'bg-amber-50/80 border-amber-300'
                        : dp.proximity_status === 'past_destination'
                        ? 'bg-red-50/80 border-red-400'
                        : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-base text-slate-900 flex items-center gap-1">
                            <MapPin className="h-4 w-4 text-panashe-red" />
                            {dp.dropoff_point}
                          </span>
                          <Badge
                            className={`text-[10px] font-bold ${
                              dp.proximity_status === 'approaching'
                                ? 'bg-red-600 text-white animate-pulse'
                                : dp.proximity_status === 'at_station'
                                ? 'bg-amber-500 text-white'
                                : dp.proximity_status === 'past_destination'
                                ? 'bg-red-700 text-white animate-pulse'
                                : dp.proximity_status === 'en_route'
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-400 text-white'
                            }`}
                          >
                            {dp.proximity_status === 'approaching'
                              ? '🚨 APPROACHING (<25km)'
                              : dp.proximity_status === 'at_station'
                              ? '📍 AT STATION'
                              : dp.proximity_status === 'past_destination'
                              ? '⚠️ PASSED DESTINATION'
                              : dp.proximity_status === 'en_route'
                              ? '🚌 EN ROUTE'
                              : '📡 NO GPS TELEMETRY'}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5">
                          <span className="font-medium">{dp.route_name}</span> • Bus: <span className="font-mono">{dp.bus_registration}</span>
                          {dp.host_name && <> • Host: <span className="text-emerald-700 font-medium">{dp.host_name}</span></>}
                        </p>
                      </div>

                      <div className="flex items-center gap-3 self-end sm:self-center">
                        {dp.eta_minutes !== null && dp.eta_minutes !== undefined && (
                          <div className="text-right">
                            <p className="text-sm font-bold text-indigo-900">
                              ~{dp.eta_minutes} min ETA
                            </p>
                            <p className="text-[11px] text-slate-500">{dp.distance_km} km away</p>
                          </div>
                        )}
                        <Button
                          size="sm"
                          className={`text-xs font-semibold ${
                            dp.proximity_status === 'approaching'
                              ? 'bg-red-600 hover:bg-red-700 text-white shadow'
                              : 'btn-panashe'
                          }`}
                          onClick={() => handleDropoffNotify(dp.trip_id, dp.dropoff_point)}
                          disabled={notifyingDropoff === dp.dropoff_point}
                        >
                          {notifyingDropoff === dp.dropoff_point ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                              Sending SMS...
                            </>
                          ) : (
                            <>
                              <BellRing className="h-3.5 w-3.5 mr-1" />
                              Send Proximity Alert
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Luggage breakdown */}
                    <div className="mt-2 pt-2 border-t border-slate-200/60 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div>
                          <span className="text-slate-500 font-medium">Luggage Parcels ({dp.total_parcels}) • On Bus: {dp.on_bus_luggage}:</span>
                        <div className="mt-1 space-y-1">
                          {(dp.luggage || []).map((l, li) => (
                            <div key={li} className="flex items-center justify-between bg-white px-2 py-1 rounded border text-[11px]">
                              <span className="font-mono font-bold text-purple-700">{l.luggage_ref}</span>
                              <span className="text-slate-600 truncate max-w-[120px]">{l.description}</span>
                              <span className="text-slate-500">Recv: {l.receiver_name}</span>
                              {l.past_destination && (
                                <span className="text-red-600 font-bold">Passed destination</span>
                              )}
                              {l.receiver_notified && (
                                <span className="text-green-600 font-bold">✓ Alerted</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between items-center text-slate-500 font-medium">
                          <span>Passengers Alighting ({dp.passengers?.length || 0}):</span>
                          {dp.total_collectible > 0 && (
                            <span className="text-emerald-700 font-bold">
                              Collect: {dp.currency === 'ZAR' ? 'R' : '$'}{dp.total_collectible.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 space-y-1">
                          {(dp.passengers || []).slice(0, 3).map((p, pi) => (
                            <div key={pi} className="flex items-center justify-between bg-white px-2 py-1 rounded border text-[11px]">
                              <span className="font-medium text-slate-800">{p.name}</span>
                              <span className="text-slate-500">Seat {p.seat}</span>
                              {p.phone && (
                                <span className="text-slate-400 font-mono">{p.phone}</span>
                              )}
                            </div>
                          ))}
                          {(dp.passengers?.length || 0) > 3 && (
                            <p className="text-[11px] text-slate-400 text-center">
                              +{dp.passengers.length - 3} more passengers
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Trips */}
        <Card className="card-panashe">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading">Today's Departures</CardTitle>
            <Badge variant="outline">{todayTrips.length} trips</Badge>
          </CardHeader>
          <CardContent>
            {todayTrips.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bus className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                <p>No trips scheduled for today</p>
              </div>
            ) : (
              <div className="space-y-3">
                {todayTrips.filter(Boolean).map((trip) => (
                  <div 
                    key={trip.id || Math.random()}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                    onClick={() => trip?.id && navigate(`/dashboard/manifest/${trip.id}`)}
                    data-testid={`trip-row-${trip.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-panashe-blue rounded-lg flex items-center justify-center text-white">
                        <Clock className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {trip.origin} → {trip.destination}
                        </p>
                        <p className="text-sm text-slate-500">
                          {format(parseISO(trip.departure_datetime), 'HH:mm')} • {trip.bus_registration}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium text-slate-900">
                          {trip.total_seats - trip.available_seats}/{trip.total_seats}
                        </p>
                        <p className="text-sm text-slate-500">seats booked</p>
                        <p className="text-xs text-slate-500">{trip.available_seats} seats left</p>
                      </div>
                      <Badge 
                        className={
                          trip.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          trip.status === 'in_transit' ? 'bg-green-100 text-green-700' :
                          'bg-slate-100 text-slate-700'
                        }
                      >
                        {trip.status}
                      </Badge>
                      <ArrowRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pending Check-ins Alert */}
        {stats?.pending_checkins > 0 && (
          <Card className="card-panashe border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900">
                    {stats.pending_checkins} passengers pending check-in
                  </p>
                  <p className="text-sm text-slate-600">
                    Ready to scan their tickets
                  </p>
                </div>
              </div>
              <Button 
                size="sm"
                onClick={() => navigate('/dashboard/scanner')}
              >
                Start Scanning
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
